import React from "react";
import { AxiosService } from "../Services/AxiosService";
import { useAuth } from "react-oidc-context";

/**
 * A custom hook for handling server requests. It fetches data from the server based on the specified type and value,
 * and returns the data, error, and loading state.
 * 
 * @function
 * @example
 * const { data, error, isLoading } = useAxios("myTasks");
 * @param {string} type - The type of request to make (e.g., "myTasks", "allTasks", "processDetail", etc.).
 * @param {*} [value=null] - An optional value required for certain requests (e.g., an ID for detail requests).
 * @returns {Object} An object containing the data, error, and loading state.
 * @returns {*} return.data - The data returned from the server.
 * @returns {string} return.error - The error message if the request fails.
 * @returns {boolean} return.isLoading - A boolean indicating whether the request is still loading.
 */
function useAxios(type, value = null) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const auth = useAuth();
  const token = auth.user?.access_token;  
  let concatenated = [];
  let results = {};

  React.useEffect(() => {
    switch (type) {
      case "employeeSubprocess":
        AxiosService.employee_subprocess(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;

      case "subprocessDetail":
        if (value !== null) {
          AxiosService.employee_subprocess_detail(token, value).then((response) => {
            setData(response.data);
          })
          .catch((err) => {
            setError(err.message);
          })
          .finally(() => {
            setIsLoading(false);
          });
        }
      break;

      case "processDetail":        
        if (value !== null) {
          AxiosService.employee_process_detail(token, value).then((response) => {
            setData(response.data);
          })
          .catch((err) => {
            setError(err.message);
          })
          .finally(() => {
            setIsLoading(false);
          });
        }
      break;



      //$$
      case "processes":
        AxiosService.processes(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;

      case "allSubprocesses":
        AxiosService.subprocesses(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;

      case "allSubprocessesDetails":
        setIsLoading(true);

        AxiosService.subprocesses(token)
          .then((response) => {
        const subprocesses = response.data;
        const detailPromises = subprocesses.map((sub) => {
          return AxiosService.employee_subprocess_detail(token, sub.id);
        });

        return Promise.all(detailPromises);
      })
      .then((detailsResponses) => {
        const allDetails = detailsResponses.map((res) => res.data);
        setData(allDetails);
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
      break;

      case "employeeDetail":
        AxiosService.employee_detail(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;
      
      case "myTasks":
        AxiosService.employee_subprocess(token).then((response) => {
          concatenated = [...response.data.map((item) =>{return {...item, type: "subprocess", end: new Date(item.endDate), start: new Date(item.endDate), allDay: true}})];
          AxiosService.processes(token).then((response) => {
            concatenated = concatenated.concat([...response.data.map((item) =>{return {...item, type: "process", end: new Date(item.endDate), start: new Date(item.endDate), allDay: true}})]);
          })
          .catch((err) => {
            setError(err.message);
          })
          .finally(() => {
            setData(concatenated);
            setIsLoading(false);
          });
        })
        .catch((err) => {
          setError(err.message);
          setIsLoading(false);
        });
      break;
      
      case "allTasks":
        AxiosService.subprocesses(token).then((response) => {
          concatenated = [...response.data.map((item) =>{return {...item, type: "subprocess", end: new Date(item.endDate), start: new Date(item.endDate), allDay: true}})];
          AxiosService.processes(token).then((response) => {
            concatenated = concatenated.concat([...response.data.map((item) =>{return {...item, type: "process", end: new Date(item.endDate), start: new Date(item.endDate), allDay: true}})]);
          })
          .catch((err) => {
            setError(err.message);
          })
          .finally(() => {
            setData(concatenated);
            setIsLoading(false);
          });
        })
        .catch((err) => {
          setError(err.message);
          setIsLoading(false);
        });
      break;

      case "processSummaries":
        AxiosService.process_summaries(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;

      case "processStatuses":
        AxiosService.process_statuses(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;

      case "processTypes":
        AxiosService.process_types(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;      

      case "departments":
        AxiosService.departments(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;


      case "centers":
        AxiosService.centers(token).then((response) => {
          setData(response.data);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      break;



      case "departments_employees":

      Promise.all([
        AxiosService.departments(token),
        AxiosService.employees(token),
        AxiosService.centers(token)
      ])
        .then(([departments, employees, centers]) => {
          const results = {
            departments: departments.data,
            employees: employees.data,
            centers: centers.data
          };

          setData(results);
        })
        .catch((err) => {
          setError(err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });



       // AxiosService.departments(token).then((response) => {
       //   results = { departments: response.data };
//
       //   AxiosService.employees(token).then((response) => {
       //     results = { ...results,  employees: response.data };
       //     setData(results);
       //   })
       //   .catch((err) => {
       //     setError(err.message);
       //   })
       //   .finally(() => {
       //     setIsLoading(false);
       //   });
       // })
       // .catch((err) => {
       //   setIsLoading(false);
       //   setError(err.message);
       // });
      break;

      default:
        console.log("Método desconocido");
    }

  }, []);

  return { data, error, isLoading };
}

export default useAxios;